import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { MpsUserService } from '../../mps/services/mps-user.service';
import { RouteResolverService } from './route-resolver.service';

/**
 * Guard factory that checks if user has required permission(s)
 * @param permissions - Single permission or array of permissions (user needs at least one)
 */
export function hasPermission(permissions: string | string[]): CanActivateFn {
  return () => {
    const userService = inject(MpsUserService);
    const router = inject(Router);
    const routeResolver = inject(RouteResolverService);

    const user = userService.getUser();

    if (!user) {
      const fallbackRoute = routeResolver.getFirstAvailableRoute();
      return fallbackRoute ? router.navigateByUrl(fallbackRoute) : false;
    }

    const requiredPermissions = Array.isArray(permissions) ? permissions : [permissions];
    const hasRequiredPermission = requiredPermissions.some(permission =>
      user.permissions?.includes(permission) ?? false
    );

    if (!hasRequiredPermission) {
      console.warn('User does not have required permission:', permissions);
      const fallbackRoute = routeResolver.getFirstAvailableRoute();
      return fallbackRoute ? router.navigateByUrl(fallbackRoute) : false;
    }

    return true;
  };
}

/**
 * Guard that checks if user has all specified permissions
 * @param permissions - Array of permissions (user needs all of them)
 */
export function hasAllPermissions(permissions: string[]): CanActivateFn {
  return () => {
    const userService = inject(MpsUserService);
    const router = inject(Router);
    const routeResolver = inject(RouteResolverService);

    const user = userService.getUser();

    if (!user) {
      const fallbackRoute = routeResolver.getFirstAvailableRoute();
      return fallbackRoute ? router.navigateByUrl(fallbackRoute) : false;
    }

    const hasAllRequired = permissions.every(permission =>
      user.permissions?.includes(permission) ?? false
    );

    if (!hasAllRequired) {
      console.warn('User does not have all required permissions:', permissions);
      const fallbackRoute = routeResolver.getFirstAvailableRoute();
      return fallbackRoute ? router.navigateByUrl(fallbackRoute) : false;
    }

    return true;
  };
}
